import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection, useDocsForTours, useShowsForTours } from '../lib/firestore'
import { useArtists, usePeople } from '../lib/artists'
import { formatDayLong, formatTime } from '../lib/format'
import type { Artist, Show, Tour, TourCrew, WithId } from '../types/models'
import Page from '../components/Page'
import Icon from '../components/Icon'
import Poster from '../components/Poster'
import RosterTimeline from '../components/RosterTimeline'

/* ============================================================================
   The agency dashboard.

   A different question from the solo one. A tour manager opens Encore to ask
   "what's next for me"; an agency opens it to ask "where is everyone" — and
   the answer has to fit on one screen before coffee.

   Three groups, in the order they get acted on:

     ON THE ROAD    running today. What's happening right now.
     COMING UP      confirmed and not started. Nothing to do but watch.
     NOTHING BOOKED the only actionable one — this is the booking list, and
                    it is deliberately last so it reads as a to-do rather than
                    an accusation on arrival.
   ============================================================================ */

type ItineraryView = 'list' | 'calendar' | 'timeline'

const VIEWS: Array<{ value: ItineraryView; label: string }> = [
  { value: 'list', label: 'List' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'timeline', label: 'Timeline' },
]

interface RosterEntry {
  artist: WithId<Artist>
  current?: WithId<Tour>
  next?: WithId<Tour>
  tours: Array<WithId<Tour>>
}

export default function AgencyDashboard() {
  const { user, uid } = useAuth()
  const { roster: artists, orgId } = useArtists()
  const people = usePeople(orgId)
  const tours = useCollection<Tour>('tours', user ? [where('ownerId', '==', uid)] : [])
  // Only the roster's tours. Reading the whole collection is refused — see
  // `useDocsForTours`. The crew list used to do exactly that, and one denied
  // query is enough to log a permissions error on every visit to this page.
  const tourIds = useMemo(() => tours.data.map((tour) => tour.id), [tours.data])
  const shows = useShowsForTours<Show>(tourIds)
  const crew = useDocsForTours<TourCrew>('tourCrew', tourIds)

  const [view, setView] = useState<ItineraryView>('list')

  // Midnight as a number, memoised on the date rather than the instant — a
  // fresh `new Date()` every render makes every memo below recompute, which
  // is the same as having no memo at all.
  const todayMs = useMemo(() => {
    const midnight = new Date()
    midnight.setHours(0, 0, 0, 0)
    return midnight.getTime()
  }, [])

  const roster: RosterEntry[] = useMemo(() => {
    return artists.map((artist) => {
      const midnight = new Date(todayMs)
      const theirs = tours.data
        .filter((tour) => tour.artistId === artist.id)
        .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis())
      return {
        artist,
        tours: theirs,
        current: theirs.find(
          (tour) => tour.startDate.toDate() <= midnight && tour.endDate.toDate() >= midnight,
        ),
        next: theirs.find((tour) => tour.startDate.toDate() > midnight),
      }
    })
  }, [artists, tours.data, todayMs])

  const onRoad = roster.filter((entry) => entry.current)
  const upcoming = roster.filter((entry) => !entry.current && entry.next)
  const idle = roster.filter((entry) => !entry.current && !entry.next)

  // Every show across the roster in the next fortnight, in date order. The
  // thing an agency actually looks at: not per-artist, but per-day.
  const fortnight = useMemo(() => {
    const start = new Date(todayMs)
    const horizon = new Date(todayMs)
    horizon.setDate(horizon.getDate() + 14)
    const artistByTour = new Map(
      tours.data.map((tour) => [tour.id, artists.find((a) => a.id === tour.artistId)]),
    )
    return shows.data
      .filter((show) => {
        const date = show.date.toDate()
        return date >= start && date <= horizon && artistByTour.has(show.tourId)
      })
      .sort((a, b) => a.date.toMillis() - b.date.toMillis())
      .slice(0, 12)
      .map((show) => ({ show, artist: artistByTour.get(show.tourId) }))
  }, [shows.data, tours.data, artists, todayMs])

  // Every roster show with its artist attached. The list view is capped at a
  // fortnight; the calendar is not, because a month grid with three weeks
  // blanked out is worse than no calendar.
  const showsWithArtist = useMemo(() => {
    const artistByTour = new Map(
      tours.data.map((tour) => [tour.id, artists.find((a) => a.id === tour.artistId)]),
    )
    return shows.data
      .filter((show) => artistByTour.has(show.tourId))
      .map((show) => ({ show, artist: artistByTour.get(show.tourId) }))
  }, [shows.data, tours.data, artists])

  // Quarter-to-date, because a month is too short to read anything into and a
  // year hides whether things are moving.
  const quarterStart = useMemo(() => {
    const date = new Date(todayMs)
    date.setMonth(Math.floor(date.getMonth() / 3) * 3, 1)
    date.setHours(0, 0, 0, 0)
    return date
  }, [todayMs])

  const rosterTourIds = useMemo(
    () => new Set(tours.data.filter((tour) => tour.artistId).map((tour) => tour.id)),
    [tours.data],
  )

  const quarterShows = shows.data.filter(
    (show) =>
      rosterTourIds.has(show.tourId) &&
      show.date.toDate() >= quarterStart &&
      show.date.toDate() <= new Date(todayMs),
  )
  const playedThisQuarter = quarterShows.length
  const citiesThisQuarter = new Set(quarterShows.map((show) => show.city).filter(Boolean)).size

  const rosterTours = tours.data.filter((tour) => tour.artistId)
  const roadDaysThisQuarter = rosterTours.reduce((total, tour) => {
    const start = Math.max(tour.startDate.toMillis(), quarterStart.getTime())
    const end = Math.min(tour.endDate.toMillis(), todayMs)
    return total + (end > start ? Math.round((end - start) / 86_400_000) + 1 : 0)
  }, 0)

  const averageTourDays = rosterTours.length
    ? Math.round(
        rosterTours.reduce(
          (total, tour) =>
            total +
            Math.round((tour.endDate.toMillis() - tour.startDate.toMillis()) / 86_400_000) +
            1,
          0,
        ) / rosterTours.length,
      )
    : 0

  // Who is physically out right now — the shared-crew question an agency asks
  // before promising anyone to a second act.
  const onTourNow = useMemo(() => {
    const liveTourIds = new Set(onRoad.map((entry) => entry.current!.id))
    return new Set(
      crew.data
        .filter((member) => liveTourIds.has(member.tourId))
        .map((member) => member.contactId ?? '')
        .filter(Boolean),
    )
  }, [crew.data, onRoad])

  return (
    <Page
      title="Roster"
      eyebrow={`${artists.length} artists`}
    >
      <div className="responsive-grid" style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        <Tally value={onRoad.length} label="On the road" accent />
        <Tally value={upcoming.length} label="Coming up" />
        <Tally value={idle.length} label="Nothing booked" />
        <Tally value={fortnight.length} label="Shows, next 14 days" />
      </div>

      {/* The single next thing happening anywhere on the roster. An agency
          wants one answer above the fold, not a grid to scan. */}
      {fortnight[0] && (
        <section className="stack" style={{ gap: 10 }}>
          <h2 className="t-eyebrow">Up next</h2>
          <Link
            to={`/tours/${fortnight[0].show.tourId}/shows/${fortnight[0].show.id}`}
            className="panel panel-signalled"
            style={{ display: 'block', padding: 22 }}
          >
            <div className="spread" style={{ gap: 18, alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 5, minWidth: 0 }}>
                <span className="t-eyebrow" style={{ color: 'var(--accent-text)' }}>
                  {fortnight[0].artist?.name ?? 'Roster'}
                </span>
                <span className="t-display-m truncate">{fortnight[0].show.city}</span>
                <span className="t-caption truncate">{fortnight[0].show.venueName}</span>
              </div>
              <div className="stack" style={{ gap: 3, alignItems: 'flex-end' }}>
                <span className="t-spec-caption faint">
                  {formatDayLong(fortnight[0].show.date, fortnight[0].show.timezone)}
                </span>
                {fortnight[0].show.headlinerSetTime && (
                  <span
                    className="t-timecode"
                    style={{ fontSize: '1.4rem', color: 'var(--accent-text)' }}
                  >
                    {formatTime(fortnight[0].show.headlinerSetTime, fortnight[0].show.timezone)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        </section>
      )}

      {onRoad.length > 0 && (
        <Group title="On the road" entries={onRoad} kind="current" />
      )}

      {/* ------------------------------------------- the itinerary, three ways */}
      {/*
          The same question ("what is everyone doing") answered three ways:
          the next fortnight as a list, the month as a calendar, and the roster
          as a timeline. The timeline used to be a page of its own; it belongs
          here, because nobody goes looking for a timeline, they go looking for
          the schedule and then choose how to read it.
      */}
      <section className="stack" style={{ gap: 10 }}>
        <div className="spread" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 className="t-eyebrow">Itinerary</h2>
          <div className="row" style={{ gap: 4 }} role="tablist" aria-label="Itinerary view">
            {VIEWS.map((option) => {
              const on = view === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className="chip"
                  onClick={() => setView(option.value)}
                  style={{
                    cursor: 'pointer',
                    background: on ? 'var(--accent-wash)' : 'transparent',
                    border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                    color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
                  }}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </div>

        {view === 'list' &&
          (fortnight.length > 0 ? (
            <div className="panel rows">
              {fortnight.map(({ show, artist }) => (
                <Link
                  key={show.id}
                  to={`/tours/${show.tourId}/shows/${show.id}`}
                  className="row"
                  style={{ padding: '12px 16px', gap: 14 }}
                >
                  <span className="t-spec-caption faint" style={{ minWidth: 92 }}>
                    {formatDayLong(show.date, show.timezone)}
                  </span>
                  <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 600 }}>
                      {show.city}
                    </span>
                    <span className="t-caption truncate">
                      {artist?.name ? `${artist.name} · ` : ''}
                      {show.venueName}
                    </span>
                  </span>
                  {show.headlinerSetTime && (
                    <span className="t-timecode">
                      {formatTime(show.headlinerSetTime, show.timezone)}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="panel panel-pad t-caption">Nothing in the next fortnight.</p>
          ))}

        {view === 'calendar' && <RosterCalendar shows={showsWithArtist} />}

        {view === 'timeline' && <RosterTimeline />}
      </section>

      {upcoming.length > 0 && <Group title="Coming up" entries={upcoming} kind="next" />}

      {/* ------------------------------------------------- team & numbers */}
      <div
        className="responsive-grid"
        style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
      >
        <section className="stack" style={{ gap: 10 }}>
          <div className="spread" style={{ alignItems: 'baseline' }}>
            <h2 className="t-eyebrow">Your team</h2>
            <span className="t-caption">{people.length} in the organisation</span>
          </div>
          {people.length === 0 ? (
            <p className="panel panel-pad t-caption">
              Nobody saved yet. People added to an artist's touring party show up here, and
              their passports and preferences are entered once for the whole roster.
            </p>
          ) : (
            <div className="panel rows">
              {people.slice(0, 8).map((person) => (
                <div key={person.id} className="row" style={{ padding: '11px 16px', gap: 12 }}>
                  <Icon name="account" size={15} style={{ color: 'var(--text-secondary)' }} />
                  <span className="grow truncate" style={{ fontWeight: 500 }}>
                    {person.name}
                  </span>
                  <span className="t-spec-caption faint truncate">
                    {onTourNow.has(person.id) ? 'Out now' : person.email ?? ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="stack" style={{ gap: 10 }}>
          <h2 className="t-eyebrow">This quarter</h2>
          <div className="panel rows">
            <Metric label="Shows played" value={playedThisQuarter} />
            <Metric label="Cities" value={citiesThisQuarter} />
            <Metric label="Days on the road" value={roadDaysThisQuarter} />
            <Metric
              label="Average tour length"
              value={averageTourDays}
              suffix={averageTourDays === 1 ? 'day' : 'days'}
            />
            <Metric label="Crew placements" value={crew.data.length} />
          </div>
        </section>
      </div>

      {idle.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <div className="spread" style={{ alignItems: 'baseline' }}>
            <h2 className="t-eyebrow">Nothing booked</h2>
            <span className="t-caption">The booking list</span>
          </div>
          <div className="panel rows">
            {idle.map((entry) => (
              <Link
                key={entry.artist.id}
                to={`/artists/${entry.artist.id}`}
                className="row"
                style={{ padding: '12px 16px', gap: 12 }}
              >
                <Poster url={entry.artist.avatarURL} width={34} />
                <span className="grow truncate" style={{ fontWeight: 600 }}>
                  {entry.artist.name}
                </span>
                <span className="t-spec-caption faint">
                  {entry.tours.length
                    ? `Last out ${formatDayLong(entry.tours[entry.tours.length - 1].endDate)}`
                    : 'No tours yet'}
                </span>
                <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
              </Link>
            ))}
          </div>
        </section>
      )}
    </Page>
  )
}

/* ============================================================================
   Calendar view

   A month at a time, Monday first. Cells are sized by the week, not by their
   contents, so the grid never reflows as you page through months; a day with
   four shows scrolls inside its own cell instead.
   ============================================================================ */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function RosterCalendar({
  shows,
}: {
  shows: Array<{ show: WithId<Show>; artist?: WithId<Artist> }>
}) {
  const [monthStart, setMonthStart] = useState(() => {
    const date = new Date()
    date.setDate(1)
    date.setHours(0, 0, 0, 0)
    return date
  })

  // Shows keyed by local day. Grouping once beats filtering inside 42 cells.
  const byDay = useMemo(() => {
    const map = new Map<string, Array<{ show: WithId<Show>; artist?: WithId<Artist> }>>()
    shows.forEach((entry) => {
      const key = dayKey(entry.show.date.toDate())
      const list = map.get(key)
      if (list) list.push(entry)
      else map.set(key, [entry])
    })
    return map
  }, [shows])

  // Six weeks always. A month that needs five and a month that needs six would
  // otherwise change the height of the whole dashboard as you page.
  const cells = useMemo(() => {
    const first = new Date(monthStart)
    const offset = (first.getDay() + 6) % 7 // Monday-first
    const start = new Date(first)
    start.setDate(first.getDate() - offset)
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start)
      date.setDate(start.getDate() + index)
      return date
    })
  }, [monthStart])

  const todayKey = dayKey(new Date())
  const month = monthStart.getMonth()

  function shift(by: number) {
    setMonthStart((current) => {
      const next = new Date(current)
      next.setMonth(next.getMonth() + by, 1)
      return next
    })
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="spread" style={{ alignItems: 'center' }}>
        <span className="t-display-s">
          {monthStart.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
        </span>
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="btn btn-quiet" onClick={() => shift(-1)} aria-label="Previous month">
            <Icon name="chevronLeft" size={15} />
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => shift(1)} aria-label="Next month">
            <Icon name="chevronRight" size={15} />
          </button>
        </div>
      </div>

      <div className="panel" style={{ overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            borderBottom: 'var(--hairline-width) solid var(--hairline)',
          }}
        >
          {WEEKDAYS.map((day) => (
            <span
              key={day}
              className="t-spec-caption faint"
              style={{ padding: '8px 10px', textAlign: 'center' }}
            >
              {day}
            </span>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {cells.map((date) => {
            const key = dayKey(date)
            const entries = byDay.get(key) ?? []
            const outside = date.getMonth() !== month
            const today = key === todayKey
            return (
              <div
                key={key}
                style={{
                  minHeight: 92,
                  padding: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  overflow: 'hidden',
                  borderRight: 'var(--hairline-width) solid var(--hairline)',
                  borderBottom: 'var(--hairline-width) solid var(--hairline)',
                  background: today ? 'var(--accent-wash)' : 'transparent',
                  opacity: outside ? 0.4 : 1,
                }}
              >
                <span
                  className="t-spec-caption"
                  style={{
                    color: today ? 'var(--accent-text)' : 'var(--text-tertiary)',
                    fontWeight: today ? 700 : 400,
                  }}
                >
                  {date.getDate()}
                </span>
                {entries.slice(0, 3).map(({ show, artist }) => (
                  <Link
                    key={show.id}
                    to={`/tours/${show.tourId}/shows/${show.id}`}
                    className="truncate"
                    style={{
                      padding: '3px 6px',
                      borderRadius: 5,
                      background: 'var(--panel-raised)',
                      borderLeft: '2px solid var(--accent)',
                      fontSize: '0.7rem',
                      lineHeight: 1.3,
                    }}
                    title={`${artist?.name ? `${artist.name} · ` : ''}${show.city}`}
                  >
                    {show.city || artist?.name || 'Show'}
                  </Link>
                ))}
                {entries.length > 3 && (
                  <span className="t-spec-caption faint">+{entries.length - 3}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** Local calendar day, so a show never lands on the wrong cell near midnight. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function Group({
  title,
  entries,
  kind,
}: {
  title: string
  entries: RosterEntry[]
  kind: 'current' | 'next'
}) {
  return (
    <section className="stack" style={{ gap: 10 }}>
      <h2 className="t-eyebrow">{title}</h2>
      <div
        className="responsive-grid"
        style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
      >
        {entries.map((entry) => {
          const tour = kind === 'current' ? entry.current : entry.next
          if (!tour) return null
          return (
            <Link
              key={entry.artist.id}
              to={`/tours/${tour.id}`}
              className={`panel${kind === 'current' ? ' panel-signalled' : ''}`}
              style={{ display: 'block', padding: 16 }}
            >
              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <Poster url={tour.posterURL ?? entry.artist.avatarURL} width={46} />
                <div className="grow stack" style={{ gap: 3, minWidth: 0 }}>
                  <span className="t-eyebrow truncate">{entry.artist.name}</span>
                  <span className="truncate" style={{ fontWeight: 600 }}>
                    {tour.tourName}
                  </span>
                  <span className="t-spec-caption faint">
                    {kind === 'current'
                      ? `Until ${formatDayLong(tour.endDate)}`
                      : `From ${formatDayLong(tour.startDate)}`}
                  </span>
                </div>
              </div>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function Tally({
  value,
  label,
  accent,
}: {
  value: number
  label: string
  accent?: boolean
}) {
  return (
    <div className="panel stack" style={{ gap: 3, padding: '16px 18px' }}>
      <span className="t-display-m" style={{ color: accent ? 'var(--accent-text)' : undefined }}>
        {value}
      </span>
      <span className="t-eyebrow">{label}</span>
    </div>
  )
}

function Metric({
  label,
  value,
  suffix,
}: {
  label: string
  value: number
  suffix?: string
}) {
  return (
    <div className="spread" style={{ padding: '11px 16px' }}>
      <span className="t-caption">{label}</span>
      <span className="t-timecode">
        {value.toLocaleString()}
        {suffix ? ` ${suffix}` : ''}
      </span>
    </div>
  )
}
